import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { renewToken, forceLogout, isLoggedOutLocked, isHardAuthFailure } from "./AuthService";
import { isPublicRoute } from "./publicRoutes";

// Axios response-error handler.
//
// 401 path:
//   1. If the original request has already been retried, or we're already
//      tearing down, fail fast (fire-and-forget forceLogout) so callers
//      don't hang waiting for the logout redirect.
//   2. Dedupe concurrent 401s against a single in-flight renewal promise.
//   3. On renewal success, trust the returned user's access_token directly
//      for the retry — don't re-read Redux, which may not have absorbed the
//      addUserLoaded dispatch yet.
//   4. On renewal failure or a still-401 retry, fire forceLogout and reject
//      to the caller.
//
// Non-401 path preserves the prior body-parsing and the OnboardingBlocked
// redirect that the app depends on.

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<any> | null = null;
let onboardingRedirectInFlight = false;

function onCallbackRoute(): boolean {
    try {
        const p = (window.location?.pathname || '').toLowerCase();
        return p.startsWith('/logincallback') || p.startsWith('/silent-renew') || p.startsWith('/logoutcallback');
    } catch {
        return false;
    }
}

async function parseErrorBody(response: any): Promise<any> {
    if (!response || response.data == null) return response;
    let result = response.data;
    if (result instanceof Blob) {
        if (result.size === 0) return response;
        if (result.type === 'application/json') {
            try {
                const text = await result.text();
                result = JSON.parse(text);
            } catch {
                return result;
            }
        }
    }
    return result;
}

// Module-level lock-toast guard. The backend TierEnforcementMiddleware
// returns `{ error: "feature_locked", ... }` on 403 for any locked-tier API.
// Several queries can 403 simultaneously (e.g. a dashboard loading multiple
// widgets), so we throttle the toast/redirect so the user sees a single,
// helpful message instead of a cascade.
let featureLockedToastInFlight = false;

// Background/global polls that ALL users (including Free) make at boot or on a
// timer. The user didn't navigate to attendance — these calls just happen to
// touch a tier-locked endpoint as a side-effect of the app initializing. Don't
// fire the global "feature isn't available" toast for these, or every Free user
// sees an unsolicited upgrade nag on every page load.
const SUPPRESS_TOAST_PATHS: readonly RegExp[] = [
    /shift\/AnyUserWithVariableScheduleRule/i,
];

function maybeShowFeatureLockedToast(response: unknown, result: unknown): void {
    const status = (response as { status?: number } | null | undefined)?.status;
    if (status !== 403) return;
    // Pattern A: structured top-level body { error: "feature_locked", ... }.
    // Pattern B: shaped through Result<T>.errors[].code === "feature_locked"
    //   (we don't currently emit this, but tolerate it for forward-compat).
    const topLevel = (result && typeof result === 'object')
        ? (result as { error?: unknown; errors?: unknown })
        : null;
    const errsRaw = topLevel && Array.isArray(topLevel.errors) ? topLevel.errors : [];
    const errsArr = errsRaw as Array<{ code?: unknown } | null | undefined>;
    const isLocked =
        (topLevel && topLevel.error === 'feature_locked') ||
        errsArr.some((e) => e?.code === 'feature_locked' || e?.code === 'FeatureLocked');
    if (!isLocked) return;

    // Skip the toast for background polls (see SUPPRESS_TOAST_PATHS above).
    const requestUrl =
        ((response as { config?: { url?: string; baseURL?: string } } | null | undefined)?.config?.url) ?? '';
    if (SUPPRESS_TOAST_PATHS.some((re) => re.test(requestUrl))) return;

    if (featureLockedToastInFlight) return;
    if (typeof window === 'undefined') return;

    // Lazy require so this module stays free of side-effects when not used.
    try {
        const url = window.location?.pathname ?? '';
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const analytics = require('./analytics') as typeof import('./analytics');
        analytics.track(analytics.AnalyticsEvents.FeatureLockedHit, { path: url });
    } catch {
        /* analytics best-effort */
    }

    featureLockedToastInFlight = true;
    try {
        // Lightweight inline toast — we can't use the React useToast hook from
        // an axios interceptor, so we render a single DOM element that the
        // user can dismiss. The toast offers a "Start trial" path; clicking
        // it navigates to /start-trial (HR/Admin-protected at the route).
        const toast = document.createElement('div');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.style.cssText = [
            'position:fixed',
            'bottom:1rem',
            'inset-inline-end:1rem',
            'z-index:9999',
            'display:flex',
            'align-items:center',
            'gap:0.75rem',
            'background:#1e293b',
            'color:white',
            'padding:0.75rem 1rem',
            'border-radius:0.5rem',
            'box-shadow:0 10px 25px -5px rgba(0,0,0,0.25)',
            'max-width:min(28rem, 90vw)',
            'font:500 0.875rem/1.25rem ui-sans-serif,system-ui,sans-serif',
        ].join(';');

        const message = document.createElement('span');
        message.textContent = "This feature isn't available on the Free plan.";
        toast.appendChild(message);

        const action = document.createElement('button');
        action.type = 'button';
        action.textContent = 'Start trial';
        action.style.cssText = [
            'background:#f59e0b',
            'color:#1e293b',
            'border:0',
            'padding:0.35rem 0.75rem',
            'border-radius:0.375rem',
            'font-weight:600',
            'cursor:pointer',
            'white-space:nowrap',
        ].join(';');
        action.addEventListener('click', () => {
            try {
                window.history.pushState({}, '', '/start-trial');
                window.dispatchEvent(new PopStateEvent('popstate'));
            } catch {
                window.location.assign('/start-trial');
            }
            cleanup();
        });
        toast.appendChild(action);

        const close = document.createElement('button');
        close.type = 'button';
        close.setAttribute('aria-label', 'Dismiss');
        close.textContent = '×';
        close.style.cssText = 'background:transparent;border:0;color:white;font-size:1.25rem;line-height:1;cursor:pointer;padding:0 0.25rem;';
        close.addEventListener('click', () => cleanup());
        toast.appendChild(close);

        const cleanup = () => {
            try { toast.remove(); } catch { /* ignore */ }
            featureLockedToastInFlight = false;
        };

        document.body.appendChild(toast);
        // Auto-dismiss after 7s so it doesn't linger if the user ignores it.
        setTimeout(cleanup, 7000);
    } catch {
        // If DOM access fails for any reason, just unlock the flag so a
        // future 403 can try again — never throw from the interceptor.
        featureLockedToastInFlight = false;
    }
}

function maybeRedirectToOnboarding(response: any, result: any): void {
    // Parallel dashboard queries can all 403 within the same tick. Without
    // this guard, each response fires window.location.assign, piling up
    // aborted navigations and producing a reload storm users read as an
    // "infinite loop". Only the first 403 in a batch should redirect.
    if (onboardingRedirectInFlight) return;
    try {
        const errs = (result as any)?.errors;
        const isBlocked = response?.status === 403 && Array.isArray(errs)
            && errs.some((e: any) => e?.code === 'OnboardingBlocked');
        if (isBlocked && typeof window !== 'undefined' && window.location) {
            const path = window.location.pathname || '';
            // The Pending Actions inbox is an allowed destination for a blocked
            // user (it lists the very terms/onboarding items they must resolve —
            // see OnboardingGate.ALLOWED_PATHS). Its MasterLayout still fires
            // background queries (subscription, platform-terms, shift checks)
            // that legitimately 403 while blocked; those must not bounce the
            // user off the inbox, or the "resolve it from the inbox" flow breaks.
            if (!path.startsWith('/onboarding/') && !path.startsWith('/terms-and-policies/') && !path.startsWith('/pending-actions')) {
                onboardingRedirectInFlight = true;
                window.location.assign('/onboarding/complete');
            }
        }
    } catch {
        // ignore redirect failures
    }
}

const errorHandling = async (error: AxiosError): Promise<any> => {
    const response = error.response as any;
    const originalRequest = error.config as RetryConfig | undefined;

    // ---- 401: attempt reactive token renewal ----
    if (response?.status === 401 && originalRequest) {
        // Skip renewal on OIDC callback routes — those handle their own
        // auth state; a forceLogout here would fight the callback.
        if (onCallbackRoute()) {
            return Promise.reject(await parseErrorBody(response));
        }

        // Never hijack an anonymous visitor on a public route (e.g. /p/<slug>).
        // A stale token in localStorage can cause a background fetch to 401;
        // forceLogout → signinRedirect would bounce the visitor to
        // /Account/Login, which is exactly the bug this guard prevents.
        if (isPublicRoute()) {
            return Promise.reject(await parseErrorBody(response));
        }

        // Already retried once, or a logout is already in flight → give up.
        if (originalRequest._retry || isLoggedOutLocked()) {
            void forceLogout('retry-after-renew-still-401');
            return Promise.reject(await parseErrorBody(response));
        }

        if (!refreshPromise) {
            refreshPromise = renewToken().finally(() => {
                refreshPromise = null;
            });
        }

        try {
            const user = await refreshPromise;
            if (!user?.access_token) {
                void forceLogout('renew-returned-no-token');
                return Promise.reject(await parseErrorBody(response));
            }
            originalRequest._retry = true;
            originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${user.access_token}`,
            };
            return axios(originalRequest);
        } catch (renewErr) {
            // Only log the user out when the IdP tells us the session is
            // truly gone (login_required / interaction_required / invalid_grant).
            // Transient failures — iframe timeout, network blip, backend
            // cold-starting, CORS on silent-renew.html — must NOT force logout;
            // they leave the stale token in place and the next 401 will try
            // again. Forcing logout on transient errors is the bug that kicks
            // users out on flaky wifi or during a 10-second redeploy.
            if (isHardAuthFailure(renewErr)) {
                void forceLogout('renew-threw-hard');
            } else {
                console.warn('[auth] silent-renew threw a transient error; leaving session in place:', renewErr);
            }
            return Promise.reject(renewErr);
        }
    }

    // ---- Non-401: preserve body parsing + OnboardingBlocked redirect ----
    if (response && response.data != null && !(response.data instanceof Blob && response.data.size === 0)) {
        const result = await parseErrorBody(response);
        maybeRedirectToOnboarding(response, result);
        // Phase 5: surface the backend tier-lock with a single toast so direct
        // API consumers (cached pages, deep-link navigations) get a clear
        // explanation instead of a silent 403.
        maybeShowFeatureLockedToast(response, result);
        return Promise.reject(result);
    }

    return Promise.reject(response ?? error);
};

export default errorHandling;
